import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, TrendingUp, ArrowRight } from 'lucide-react';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { CircularProgress } from '@/components/ui/circular-progress';
import { format, addDays } from 'date-fns';

const WellnessWidget: React.FC = () => {
  const { familyId } = useCurrentUserFamily();

  // Helper functions for wellness metrics
  const getScoreType = (key: string): 'higher-better' | 'lower-better' | 'moderate-better' => {
    if (key === 'processed_food_frequency' || key === 'saturated_fat_moderation') {
      return 'lower-better';
    }
    if (key === 'carb_intake') {
      return 'moderate-better';
    }
    return 'higher-better'; // fiber_intake, vegetable_diversity, plant_to_animal_balance
  };

  const getShortLabel = (key: string): string => {
    const labels: Record<string, string> = {
      fiber_intake: 'Fiber',
      vegetable_diversity: 'Vegetables',
      plant_to_animal_balance: 'Plant/Animal',
      carb_intake: 'Carbs',
      saturated_fat_moderation: 'Sat Fat',
      processed_food_frequency: 'Processed'
    };
    return labels[key] || key;
  };

  // Fetch latest wellness assessment
  const { data: latestAssessment, isLoading } = useQuery({
    queryKey: ['latest-wellness-assessment', familyId],
    queryFn: async () => {
      if (!familyId) return null;
      
      const { data, error } = await supabase
        .from('weekly_wellness_assessments')
        .select('*')
        .eq('family_id', familyId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    },
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Parse wellness data if available
  const wellnessData = latestAssessment?.wellness_analysis as any;
  const weeklyScore = wellnessData?.overall_wellness_score;
  const scores = wellnessData?.scores;
  const hasData = latestAssessment && wellnessData && scores;

  const wellnessMetrics = [
    'fiber_intake',
    'vegetable_diversity', 
    'plant_to_animal_balance',
    'carb_intake',
    'saturated_fat_moderation',
    'processed_food_frequency'
  ];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          {/* Grid of skeleton circles */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="flex flex-col items-center space-y-2">
                <Skeleton className="w-12 h-12 rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        {/* Week information and score */}
        <div className="flex items-center justify-between mb-4">
          {hasData && latestAssessment?.week_start_date ? (
            <div className="text-sm text-muted-foreground">
              Week of {format(new Date(latestAssessment.week_start_date), 'MMM d')} - {format(addDays(new Date(latestAssessment.week_start_date), 6), 'MMM d')}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Latest week
            </div>
          )}
          {hasData && weeklyScore && (
            <div className="flex items-center text-sm font-medium">
              <TrendingUp className="h-4 w-4 text-emerald-500 mr-1" />
              <span className="text-emerald-600">{weeklyScore.toFixed(1)}/10</span>
            </div>
          )}
        </div>

        {hasData ? (
          <div className="space-y-4">
            {/* Mini circular progress grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {wellnessMetrics.map((metric) => {
                const score = scores[metric];
                const scoreType = getScoreType(metric);
                const label = getShortLabel(metric);
                
                return (
                  <div key={metric} className="flex flex-col items-center space-y-2">
                    <CircularProgress
                      value={score * 20} // Convert 1-5 score to 0-100 percentage
                      rawScore={score} // Pass raw score for color coding
                      size={48}
                      strokeWidth={4}
                      scoreType={scoreType}
                      showValue={false}
                    />
                    <span className="text-xs font-medium text-center text-muted-foreground">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Start planning meals and track your family's wellness journey with personalized insights.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WellnessWidget;